import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ShieldCheck, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Security() {
  const { user } = useAuth();
  const [status, setStatus] = useState({ mfa_enabled: false });
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/auth/mfa/status");
    setStatus(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const startSetup = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setSetupData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot start MFA setup");
    } finally { setLoading(false); }
  };

  const confirmSetup = async () => {
    setLoading(true);
    try {
      await api.post("/auth/mfa/verify-setup", { code });
      toast.success("MFA enabled");
      setSetupData(null);
      setCode("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code");
    } finally { setLoading(false); }
  };

  const disable = async () => {
    if (!code) return toast.error("Enter your current 6-digit code first");
    setLoading(true);
    try {
      await api.post("/auth/mfa/disable", { code });
      toast.success("MFA disabled");
      setCode("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code");
    } finally { setLoading(false); }
  };

  const eligible = user?.role === "admin" || user?.role === "cpa";

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="font-[Chivo] text-3xl font-black text-[#0A2540]" data-testid="security-title">Security</h1>
        <p className="text-slate-600 mt-1">Multi-factor authentication for firm accounts.</p>
      </div>

      {!eligible ? (
        <Alert>
          <WarningCircle className="h-4 w-4" />
          <AlertTitle>Not required for client accounts</AlertTitle>
          <AlertDescription>MFA is currently reserved for admin and CPA roles. Contact your firm administrator if you'd like it enabled for your account.</AlertDescription>
        </Alert>
      ) : (
        <div className="rounded-lg border tf-hairline bg-white p-8">
          <div className="flex items-center gap-3">
            <ShieldCheck size={28} weight="duotone" className="text-[#0A2540]" />
            <div>
              <div className="font-[Chivo] font-bold text-[#0A2540]">Two-factor authentication</div>
              <div className="text-sm text-slate-500">Time-based (TOTP) via Google Authenticator, 1Password or Authy.</div>
            </div>
            <span className={`ml-auto text-xs font-mono uppercase tracking-widest px-3 py-1 rounded ${status.mfa_enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`} data-testid="mfa-status">
              {status.mfa_enabled ? "Enabled" : "Disabled"}
            </span>
          </div>

          <div className="mt-6 border-t tf-hairline pt-6">
            {!status.mfa_enabled && !setupData && (
              <Button onClick={startSetup} disabled={loading} className="bg-[#D32F2F]" data-testid="mfa-start-setup">
                {loading ? "Preparing…" : "Enable MFA"}
              </Button>
            )}

            {setupData && (
              <div className="space-y-4" data-testid="mfa-setup-panel">
                <p className="text-sm text-slate-600">Scan this QR code with your authenticator app, then enter the 6-digit code to confirm.</p>
                <img src={setupData.qr_code_b64} alt="MFA QR" className="w-48 h-48 border tf-hairline rounded" />
                <div className="text-xs text-slate-500 font-mono break-all">Manual entry secret: {setupData.secret}</div>
                <div>
                  <Label>Verification code</Label>
                  <Input value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} className="mt-1 font-mono w-40" data-testid="mfa-code" />
                </div>
                <div className="flex gap-2">
                  <Button className="bg-[#0A2540]" onClick={confirmSetup} disabled={loading || code.length !== 6} data-testid="mfa-confirm">Confirm & enable</Button>
                  <Button variant="outline" onClick={() => { setSetupData(null); setCode(""); }}>Cancel</Button>
                </div>
              </div>
            )}

            {status.mfa_enabled && !setupData && (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">MFA is active. To disable, enter a current 6-digit code from your authenticator.</p>
                <div className="flex gap-3 items-end">
                  <div>
                    <Label>Current code</Label>
                    <Input value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} className="mt-1 font-mono w-40" data-testid="mfa-disable-code" />
                  </div>
                  <Button variant="outline" onClick={disable} disabled={loading} data-testid="mfa-disable">Disable MFA</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
